// ⛔ LOCKED FILE — see server/payment-core/README.md
// Client API contract. Cosmetic UI changes MUST NOT touch this file.

export interface StockBadge {
  text: string;
  variant: "normal" | "low" | "oos";
}

export interface Product {
  id: number;
  slug: string;
  nameHe: string;
  nameEn: string | null;
  descriptionHe: string | null;
  vintage: number | null;
  varietal: string | null;
  priceAgorot: number;
  imageUrl: string | null;
  /** True iff the customer can add this product to the cart right now. */
  inStock: boolean;
  /** Pre-rendered badge to display, or null to render no badge at all. */
  stockBadge: StockBadge | null;
  /** Marked as a "favourite" — drives selection in the Featured block. */
  isFeatured: boolean;
  /** Wine-attribute strip. Null = attribute not shown for this wine. */
  sweetness: "dry" | "semi_dry" | null;
  kosher: "kosher" | "not_kosher" | null;
  oakAged: "oak" | "no_oak" | null;
  /** IDs of custom attributes (defined in SiteContentResponse.wineAttributes)
   *  that apply to this wine. Always present — empty array when none. */
  customAttributeIds: string[];
  /** Per-product values for "value"-kind custom attributes, keyed by id.
   *  Always present — empty object when none. */
  customAttributeValues: Record<string, string>;
}

export interface OrderItemInput {
  productId: number;
  qty: number;
}

export interface CreateOrderInput {
  customerName: string;
  email: string;
  phone: string;
  address?: { street: string; city: string; zip?: string; notes?: string };
  items: OrderItemInput[];
  notes?: string;
  couponCode?: string;
  /** Statutory consent gates — must be the literal `true` (server enforces). */
  acceptedTerms: true;
  confirmedOfAge: true;
}

export type CouponValidationResult =
  | { valid: true; code: string; discountAgorot: number; subtotalAgorot: number; totalAgorot: number }
  | { valid: false; reason: string };

export interface Coupon {
  id: number;
  code: string;
  name: string;
  discountType: "fixed" | "percent";
  discountValue: number;
  expiresAt: string;
  maxUses: number | null;
  usedCount: number;
  minPurchaseAgorot: number | null;
  maxPurchaseAgorot: number | null;
  allowedEmails: string[] | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminCouponDetail {
  coupon: Coupon;
  allowedProductIds: number[];
  excludedProductIds: number[];
}

export interface CouponInputPayload {
  code: string;
  name: string;
  discountType: "fixed" | "percent";
  discountValue: number;
  expiresAt: string; // ISO
  maxUses?: number | null;
  minPurchaseAgorot?: number | null;
  maxPurchaseAgorot?: number | null;
  allowedEmails?: string[] | null;
  isActive?: boolean;
  allowedProductIds?: number[];
  excludedProductIds?: number[];
}

export interface UnavailableItem {
  productId: number;
  nameHe: string;
  requested: number;
  available: number;
  reason: "out_of_stock" | "unpublished" | "not_found";
}

export class ApiError extends Error {
  status: number;
  /** Full JSON body returned by the server — useful for endpoint-specific shapes (e.g., insufficient_stock.items). */
  body: { error?: string; details?: ApiError["details"]; items?: UnavailableItem[]; issues?: string[]; [key: string]: unknown };
  details?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
  /** Site-CMS PATCH returns a flat `issues` array — `path: message` strings ready to show. */
  issues?: string[];
  constructor(status: number, message: string, body: ApiError["body"] = {}) {
    super(message);
    this.status = status;
    this.body = body;
    this.details = body.details;
    this.issues = Array.isArray(body.issues) ? body.issues : undefined;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.error ?? `HTTP ${res.status}`, body);
  }
  return res.json() as Promise<T>;
}

export const api = {
  listProducts: () => request<{ products: Product[] }>("/api/products"),
  getProduct: (slug: string) => request<{ product: Product }>(`/api/products/${encodeURIComponent(slug)}`),
  createOrder: (input: CreateOrderInput) =>
    request<{
      orderId: number;
      publicId: string;
      subtotalAgorot: number;
      discountAgorot: number;
      totalAgorot: number;
      couponCode: string | null;
    }>("/api/orders", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  getOrder: (publicId: string) =>
    request<{
      order: {
        publicId: string;
        customerName: string;
        totalAgorot: number;
        discountAgorot: number;
        couponCodeSnapshot: string | null;
        status: string;
        createdAt: string;
      };
    }>(`/api/orders/${encodeURIComponent(publicId)}`),
  validateCoupon: (code: string, items: OrderItemInput[], email?: string) =>
    request<CouponValidationResult>("/api/coupons/validate", {
      method: "POST",
      body: JSON.stringify({ code, items, ...(email ? { email } : {}) }),
    }),
  initPayment: (publicId: string) =>
    request<{ provider: string; paymentUrl: string; providerRef?: string }>(
      `/api/orders/${encodeURIComponent(publicId)}/pay/init`,
      { method: "POST" },
    ),
  confirmPaymentStub: (publicId: string) =>
    request<{ status: string }>(`/api/orders/${encodeURIComponent(publicId)}/pay/confirm-stub`, {
      method: "POST",
    }),
  me: () => request<{ admin: { id: number; email: string; role: string } | null }>("/api/auth/me"),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  getSiteContent: () => request<SiteContentResponse>("/api/site/content"),

  /** Public — per-page block list (WordPress-like layout). */
  getSitePage: (slug: PageSlug) =>
    request<{ slug: PageSlug; title: string; blocks: BlockInstance[] }>(
      `/api/site/pages/${encodeURIComponent(slug)}`,
    ),
  /** Public — current theme tokens, fonts, button style. */
  getSiteTheme: () => request<SiteThemeResponse>("/api/site/theme"),

  /** Public — list of visible pages (for header nav + footer). */
  listSitePages: () =>
    request<{
      pages: { slug: string; title: string; visible: boolean; inNav: boolean; sortOrder: number }[];
    }>("/api/site/pages"),

  /** Public — submit the contact form. */
  submitContact: (payload: { name: string; email: string; phone?: string; message: string }) =>
    request<{ ok: true }>("/api/contact", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Public — submit a cancellation request for an order. */
  submitCancellation: (payload: {
    orderPublicId?: string;
    customerName: string;
    email: string;
    phone?: string;
    reason: string;
  }) =>
    request<{ ok: true }>("/api/cancellation-request", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Public — newsletter signup. Idempotent. */
  subscribeNewsletter: (payload: { email: string; source: "footer" | "contact" | "checkout" }) =>
    request<{ ok: true }>("/api/newsletter", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  /** Public — list tours with upcoming-slot summary. */
  listTours: () => request<{ tours: PublicTour[] }>("/api/tours"),
  getTour: (slug: string) =>
    request<{ tour: PublicTour; slots: PublicTourSlot[] }>(
      `/api/tours/${encodeURIComponent(slug)}`,
    ),
  validateTourCoupon: (payload: {
    tourId: number;
    participantCount: number;
    code: string;
    email?: string;
  }) =>
    request<
      | { valid: true; code: string; discountAgorot: number; subtotalAgorot: number; totalAgorot: number }
      | { valid: false; reason: string }
    >("/api/tours/validate-coupon", {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  bookTour: (payload: {
    slotId: number;
    customerName: string;
    email: string;
    phone: string;
    participantCount: number;
    notes?: string;
    couponCode?: string;
    joinNewsletter?: boolean;
  }) =>
    request<{
      ok: true;
      publicId: string;
      totalAgorot: number;
      discountAgorot: number;
      participantCount: number;
      tourName: string;
      startsAt: string;
    }>("/api/tours/book", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  admin: {
    dashboard: () =>
      request<{
        totals: { orderCount: number; revenue: number };
        recent: AdminOrder[];
        lowStock: { id: number; slug: string; nameHe: string; stockQty: number }[];
      }>("/api/admin/dashboard"),
    listOrders: () => request<{ orders: AdminOrder[] }>("/api/admin/orders"),
    getOrder: (id: number) =>
      request<{ order: AdminOrder; items: AdminOrderItem[]; events: PaymentEvent[] }>(`/api/admin/orders/${id}`),
    listPayments: (params: { status?: string; eventType?: string; q?: string } = {}) => {
      const qs = new URLSearchParams();
      for (const [k, v] of Object.entries(params)) if (v && v !== "all") qs.set(k, v);
      return request<{ events: PaymentLogEntry[] }>(`/api/admin/payments${qs.toString() ? `?${qs}` : ""}`);
    },
    updateOrder: (id: number, patch: { status?: string; notes?: string }) =>
      request<{ order: AdminOrder }>(`/api/admin/orders/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    listProducts: () => request<{ products: AdminProduct[] }>("/api/admin/products"),
    createProduct: (p: Partial<AdminProduct>) =>
      request<{ product: AdminProduct }>("/api/admin/products", { method: "POST", body: JSON.stringify(p) }),
    updateProduct: (id: number, p: Partial<AdminProduct>) =>
      request<{ product: AdminProduct }>(`/api/admin/products/${id}`, { method: "PATCH", body: JSON.stringify(p) }),
    deleteProduct: (id: number) => request<{ ok: true }>(`/api/admin/products/${id}`, { method: "DELETE" }),
    duplicateProduct: (id: number) =>
      request<{ product: AdminProduct }>(`/api/admin/products/${id}/duplicate`, { method: "POST" }),
    uploadProductImage: async (id: number, file: File) => {
      const fd = new FormData();
      fd.append("image", file);
      const res = await fetch(`/api/admin/products/${id}/image`, {
        method: "POST",
        credentials: "same-origin",
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      return res.json() as Promise<{ ok: true; imageUrl: string; sizeBytes: number }>;
    },
    deleteProductImage: (id: number) =>
      request<{ ok: true }>(`/api/admin/products/${id}/image`, { method: "DELETE" }),
    listUsers: () => request<{ users: AdminUser[] }>("/api/admin/users"),
    addUser: (email: string, role: "owner" | "admin" = "admin") =>
      request<{ user: AdminUser | null }>("/api/admin/users", { method: "POST", body: JSON.stringify({ email, role }) }),
    removeUser: (id: number) => request<{ ok: true }>(`/api/admin/users/${id}`, { method: "DELETE" }),
    audit: () => request<{ entries: AuditEntry[] }>("/api/admin/audit"),

    listCoupons: () => request<{ coupons: Coupon[] }>("/api/admin/coupons"),
    getCoupon: (id: number) => request<AdminCouponDetail>(`/api/admin/coupons/${id}`),
    createCoupon: (payload: CouponInputPayload) =>
      request<{ coupon: Coupon }>("/api/admin/coupons", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    updateCoupon: (id: number, payload: Partial<CouponInputPayload>) =>
      request<{ coupon: Coupon }>(`/api/admin/coupons/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    deleteCoupon: (id: number) =>
      request<{ ok: true }>(`/api/admin/coupons/${id}`, { method: "DELETE" }),

    tours: {
      list: () => request<{ tours: AdminTour[] }>("/api/admin/tours"),
      create: (payload: Partial<AdminTour>) =>
        request<{ tour: AdminTour }>("/api/admin/tours", {
          method: "POST",
          body: JSON.stringify(payload),
        }),
      update: (id: number, payload: Partial<AdminTour>) =>
        request<{ tour: AdminTour }>(`/api/admin/tours/${id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        }),
      remove: (id: number) =>
        request<{ ok: true }>(`/api/admin/tours/${id}`, { method: "DELETE" }),
      uploadImage: async (id: number, file: File) => {
        const fd = new FormData();
        fd.append("image", file);
        const res = await fetch(`/api/admin/tours/${id}/image`, {
          method: "POST",
          credentials: "same-origin",
          body: fd,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ ok: true; imageUrl: string }>;
      },
      listSlots: (tourId: number) =>
        request<{ slots: TourSlot[] }>(`/api/admin/tours/${tourId}/slots`),
      createSlot: (payload: {
        tourId: number;
        startsAt: string;
        capacity: number;
        notes?: string | null;
      }) =>
        request<{ slot: TourSlot }>("/api/admin/tours/slots", {
          method: "POST",
          body: JSON.stringify(payload),
        }),
      bulkSlots: (payload: {
        tourId: number;
        fromDate: string;
        toDate: string;
        weekdays: number[];
        times: string[];
        capacity: number;
      }) =>
        request<{ created: number }>("/api/admin/tours/slots/bulk", {
          method: "POST",
          body: JSON.stringify(payload),
        }),
      updateSlot: (
        id: number,
        patch: { capacity?: number; isCancelled?: boolean; notes?: string | null },
      ) =>
        request<{ slot: TourSlot }>(`/api/admin/tours/slots/${id}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
      removeSlot: (id: number) =>
        request<{ ok: true; cancelled?: boolean; deleted?: boolean }>(
          `/api/admin/tours/slots/${id}`,
          { method: "DELETE" },
        ),
      listBookings: () => request<{ bookings: AdminTourBooking[] }>("/api/admin/tours/bookings"),
      updateBooking: (
        id: number,
        patch: { status?: "pending" | "confirmed" | "cancelled"; notes?: string | null },
      ) =>
        request<{ booking: AdminTourBooking }>(`/api/admin/tours/bookings/${id}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
        }),
    },

    listChecks: () => request<{ checks: HealthCheck[] }>("/api/admin/checks"),
    getCheckDetail: (id: string) =>
      request<{ check: HealthCheck; runs: HealthCheckRun[] }>(
        `/api/admin/checks/${encodeURIComponent(id)}`,
      ),
    getCheckRun: (runId: number) =>
      request<{ run: HealthCheckRun }>(`/api/admin/checks/runs/${runId}`),
    runCheck: (id: string) =>
      request<{ runId: number; status: string; durationMs: number; output: unknown }>(
        `/api/admin/checks/${encodeURIComponent(id)}/run`,
        { method: "POST" },
      ),
    runAllChecks: () =>
      request<{ results: Array<{ id: string; status: string; durationMs: number }> }>(
        "/api/admin/checks/run-all",
        { method: "POST" },
      ),
    updateCheck: (id: string, patch: { enabled?: boolean; schedule?: string }) =>
      request<{ ok: true }>(`/api/admin/checks/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),

    pages: {
      list: () =>
        request<{
          pages: {
            slug: PageSlug;
            title: string;
            blockCount: number;
            visible: boolean;
            inNav: boolean;
            sortOrder: number;
            isCore: boolean;
            updatedAt: string | null;
          }[];
        }>("/api/admin/site/pages"),
      get: (slug: PageSlug) =>
        request<{
          slug: PageSlug;
          title: string;
          blocks: BlockInstance[];
          visible: boolean;
          inNav: boolean;
          sortOrder: number;
          isCore: boolean;
          updatedAt: string | null;
        }>(`/api/admin/site/pages/${encodeURIComponent(slug)}`),
      create: (payload: { slug: string; title: string }) =>
        request<{ ok: true; slug: string }>("/api/admin/site/pages", {
          method: "POST",
          body: JSON.stringify(payload),
        }),
      update: (slug: PageSlug, blocks: BlockInstance[]) =>
        request<{ ok: true; slug: PageSlug; blocks: BlockInstance[] }>(
          `/api/admin/site/pages/${encodeURIComponent(slug)}`,
          { method: "PUT", body: JSON.stringify({ blocks }) },
        ),
      updateMeta: (
        slug: PageSlug,
        meta: { title?: string; visible?: boolean; inNav?: boolean; sortOrder?: number },
      ) =>
        request<{ ok: true }>(
          `/api/admin/site/pages/${encodeURIComponent(slug)}/meta`,
          { method: "PUT", body: JSON.stringify(meta) },
        ),
      reset: (slug: PageSlug) =>
        request<{ ok: true; slug: PageSlug; blocks: BlockInstance[] }>(
          `/api/admin/site/pages/${encodeURIComponent(slug)}/reset`,
          { method: "POST" },
        ),
      remove: (slug: PageSlug) =>
        request<{ ok: true }>(`/api/admin/site/pages/${encodeURIComponent(slug)}`, {
          method: "DELETE",
        }),
    },
    theme: {
      get: () => request<SiteThemeResponse>("/api/admin/site/theme"),
      update: (theme: SiteThemeResponse) =>
        request<{ ok: true; theme: SiteThemeResponse }>("/api/admin/site/theme", {
          method: "PUT",
          body: JSON.stringify(theme),
        }),
    },
    site: {
      getContent: () => request<AdminSiteContentResponse>("/api/admin/site/content"),
      updateContent: <K extends SiteContentKey>(key: K, value: SiteContentMap[K]) =>
        request<{ ok: true; key: K; value: SiteContentMap[K] }>(
          `/api/admin/site/content/${encodeURIComponent(key)}`,
          { method: "PATCH", body: JSON.stringify(value) },
        ),
      resetContent: <K extends SiteContentKey>(key: K) =>
        request<{ ok: true; key: K; value: SiteContentMap[K] }>(
          `/api/admin/site/content/reset/${encodeURIComponent(key)}`,
          { method: "POST" },
        ),
      uploadImage: async (slot: string, file: File) => {
        const fd = new FormData();
        fd.append("image", file);
        const res = await fetch(`/api/admin/site/images/${encodeURIComponent(slot)}`, {
          method: "POST",
          credentials: "same-origin",
          body: fd,
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<{ ok: true; slot: string; url: string; sizeBytes: number }>;
      },
      deleteImage: (slot: string) =>
        request<{ ok: true }>(`/api/admin/site/images/${encodeURIComponent(slot)}`, {
          method: "DELETE",
        }),
    },
  },
};

export interface HealthCheck {
  id: string;
  suite: string;
  name: string;
  description: string;
  mutating: boolean;
  enabled: boolean;
  schedule: "manual" | "hourly" | "daily" | "weekly";
  isRunning: boolean;
  lastRun: {
    status: string;
    startedAt: string;
    finishedAt: string | null;
    durationMs: number | null;
  } | null;
}

export interface HealthCheckRun {
  id: number;
  checkId: string;
  triggeredBy: string;
  triggeredByEmail: string | null;
  startedAt: string;
  finishedAt: string | null;
  status: string;
  durationMs: number | null;
  output: {
    ok?: boolean;
    reason?: string;
    details?: Record<string, unknown>;
    error?: string;
  } | null;
}

export interface AdminOrder {
  id: number;
  publicId: string;
  customerName: string;
  email: string;
  phone: string;
  address: { street: string; city: string; zip?: string; notes?: string } | null;
  totalAgorot: number;
  status: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminOrderItem {
  id: number;
  productId: number;
  qty: number;
  unitPriceAgorot: number;
  nameSnapshot: string;
}

export interface AdminProduct {
  id: number;
  slug: string;
  nameHe: string;
  nameEn: string | null;
  descriptionHe: string | null;
  vintage: number | null;
  varietal: string | null;
  priceAgorot: number;
  /** Real numeric stock. Public API never exposes this — only AdminProduct does. */
  stockQty: number;
  imageUrl: string | null;
  sortOrder: number;
  isPublished: boolean;
  isFeatured: boolean;
  metaTitle: string | null;
  metaDescription: string | null;
  stockDisplayMode: "auto" | "manual" | "hidden";
  stockDisplayLabel: string | null;
  sweetness: "dry" | "semi_dry" | null;
  kosher: "kosher" | "not_kosher" | null;
  oakAged: "oak" | "no_oak" | null;
  customAttributeIds: string[] | null;
  customAttributeValues: Record<string, string> | null;
}

export interface AdminUser {
  id: number;
  email: string;
  role: string;
  createdAt: string;
}

export interface AuditEntry {
  id: number;
  actorEmail: string;
  action: string;
  entity: string;
  entityId: string | null;
  diff: unknown;
  at: string;
}

export interface PaymentEvent {
  id: number;
  orderId: number;
  eventType: "init" | "webhook" | "manual_confirm";
  provider: "manual" | "mock" | "grow";
  providerStatus: string | null;
  providerRef: string | null;
  approvalCode: string | null;
  reason: string | null;
  signatureValid: boolean | null;
  ipAddress: string | null;
  userAgent: string | null;
  customerSnapshot: {
    name?: string;
    email?: string;
    phone?: string;
    address?: { street: string; city: string; zip?: string; notes?: string } | null;
    totalAgorot?: number;
  } | null;
  rawPayload: unknown;
  createdAt: string;
}

export interface PaymentLogEntry extends PaymentEvent {
  eventId: number;
  publicId: string;
  customerName: string;
  email: string;
  phone: string;
  totalAgorot: number;
  orderStatus: string;
}

/* ─── Site CMS types ──────────────────────────────────────────────── */

export interface HeroAnimation {
  /** Extra image slot names cycled with the primary slot. Max 4. */
  extraImageSlots: string[];
  /** Cycle period (seconds). 0 = no rotation, even with multiple slots. */
  rotationSec: number;
  parallaxEnabled: boolean;
  /** 0–50 — percentage of the image that's hidden outside the visible
   *  window. The remaining slice always fills the section; on scroll, the
   *  slice slides across the image. 0 = no parallax. */
  parallaxHiddenPercent: number;
}

export interface StoryAnimation {
  parallaxEnabled: boolean;
  parallaxHiddenPercent: number;
}

/** Every section has a `visible` flag; treat undefined as visible (backwards-compat with rows seeded before the field existed). */
export interface SiteHero {
  visible: boolean;
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  subtitle: string;
  ctaShopLabel: string;
  ctaStoryLabel: string;
  /** "background" (default) — image fills the hero with bg-cover crop +
   *  gradient overlay; text overlays on top.
   *  "banner" — image displayed at natural aspect ratio above the gradient
   *  hero, no crop, no overlay. */
  imageMode?: "background" | "banner";
  /** Banner-mode responsiveness:
   *   "width"  — image edge-to-edge horizontally, height follows aspect.
   *   "height" — image always fills viewport height; sides crop or
   *              get empty margins depending on screen aspect. */
  bannerSizing?: "width" | "height";
  /** Optional — when absent, treated as defaults (no rotation, no parallax). */
  animation?: HeroAnimation;
}
export interface SiteStorySection {
  /** Optional heading shown above the body. Empty string = no heading. */
  heading: string;
  body: string;
}
export interface SiteStory {
  visible: boolean;
  eyebrow: string;
  titleLine1: string;
  titleLine2: string;
  sections: SiteStorySection[];
  ctaLabel: string;
  portraitSize?: "sm" | "md" | "lg" | "xl";
  portraitAspect?: "3/4" | "1/1" | "4/3" | "16/9" | "auto";
  animation?: StoryAnimation;
}

export interface SiteSpacer {
  visible: boolean;
  size: "xs" | "sm" | "md" | "lg" | "xl";
}

export interface SiteVideo {
  visible: boolean;
  eyebrow: string;
  title: string;
  subtitle: string;
  /** YouTube watch URL, Vimeo URL, or direct .mp4. Empty = no video set. */
  url: string;
}

export interface SiteContact {
  phone: string;
  email: string;
  whatsappPhone: string;
  addressLine1: string;
  addressLine2: string;
  hoursLine: string;
  instagramUrl: string;
  facebookUrl: string;
}

export interface SiteAgeGate {
  enabled: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  denyLabel: string;
  denyMessage: string;
}

export interface SiteAboutSection {
  heading: string;
  body: string;
  /** Name of an IMAGE_SLOT (e.g. "about-vineyard"). Empty string = no image. */
  imageSlot: string;
}
export interface SiteAbout {
  visible: boolean;
  title: string;
  intro: string;
  /** Hero portrait slot, shown right under the title. Empty = placeholder. */
  portraitSlot: string;
  sections: SiteAboutSection[];
}
export interface SiteFeatured {
  visible: boolean;
  eyebrow: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
}
export interface SiteBundleItem {
  title: string;
  desc: string;
  priceLabel: string;
  /** Optional image slot name. Empty = render the gradient placeholder. */
  imageSlot: string;
}
export interface SiteBundles {
  visible: boolean;
  eyebrow: string;
  title: string;
  subtitle: string;
  items: SiteBundleItem[];
}
export interface SiteTestimonialItem {
  quote: string;
  name: string;
  role: string;
}
export interface SiteTestimonials {
  visible: boolean;
  eyebrow: string;
  title: string;
  items: SiteTestimonialItem[];
}
export type SiteIconName = "truck" | "shield-check" | "grape" | "hand-heart";
export interface SiteFeatureItem {
  iconName: SiteIconName;
  title: string;
  desc: string;
}
export interface SiteFeatures {
  visible: boolean;
  eyebrow: string;
  title: string;
  items: SiteFeatureItem[];
}

export interface SiteLegalSection {
  heading: string;
  body: string;
}
export interface SiteNavLink {
  label: string;
  url: string;
  visible: boolean;
}

export interface SiteHeaderConfig {
  showLogo: boolean;
  showCart: boolean;
  showAccessibilityMenu: boolean;
  navLinks: SiteNavLink[];
}

export interface SiteFooterColumn {
  title: string;
  visible: boolean;
  links: SiteNavLink[];
}

export interface SiteFooterConfig {
  showLogo: boolean;
  brandText: string;
  showAddress: boolean;
  showSocialIcons: boolean;
  showAlcoholWarning: boolean;
  showCopyright: boolean;
  copyrightText: string;
  showAgeStatement: boolean;
  ageStatementText: string;
  columns: SiteFooterColumn[];
}

export interface SiteNewsletter {
  visible: boolean;
  eyebrow: string;
  title: string;
  subtitle: string;
  buttonLabel: string;
  successMessage: string;
  imageMode: "none" | "above" | "side";
  /** "right"/"left" preferred; "start"/"end" accepted for legacy data and
   *  normalised at render time (right=end, left=start). */
  imageAlign: "left" | "right" | "start" | "end";
  /** Side-mode only — how the image is sized vs the form column. */
  imageSize: "matchHeight" | "matchWidth" | "natural";
}

export interface SiteImageBanner {
  visible: boolean;
  /** "auto" = render at the image's natural aspect ratio (no crop).
   *  Anything else pins the section to a fixed height + cover crop. */
  height: "auto" | "small" | "medium" | "large" | "full";
  eyebrow: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
  ctaUrl: string;
  overlayAlign: "start" | "center" | "end";
  overlayTone: "light" | "dark";
  overlayDim: boolean;
  animation?: { parallaxEnabled: boolean; parallaxHiddenPercent: number };
}

export interface SiteLegalPage {
  visible: boolean;
  title: string;
  intro: string;
  sections: SiteLegalSection[];
}

export type SiteContentKey =
  | "hero"
  | "video"
  | "story"
  | "featured"
  | "bundles"
  | "testimonials"
  | "features"
  | "about"
  | "privacy"
  | "accessibility"
  | "terms"
  | "contact"
  | "ageGate"
  | "footerNewsletter"
  | "headerConfig"
  | "footerConfig"
  | "wineAttributes";
export type SiteImageSlot =
  | "hero"
  | "story-portrait"
  | "bundle-1"
  | "bundle-2"
  | "bundle-3"
  | "hero-video-poster"
  | "about-portrait"
  | "about-misgav-am"
  | "about-vineyard"
  | "about-history"
  | "site-logo";

/** Mirror of server/site-cms/defaults.ts IMAGE_SLOT_HINTS — shown in
 *  placeholder boxes when a slot is empty, and as hint text in the admin. */
export const SITE_IMAGE_SLOT_HINTS: Record<SiteImageSlot, string> = {
  hero: "תמונת רקע ל-hero של דף הבית — 1920×1080 (16:9). שדה / כרם / נוף.",
  "story-portrait": "תמונה צמודה לסיפור בדף הבית — 1200×1600 (3:4). פורטרט אנכי.",
  "bundle-1": "תמונת מארז 1 (אופציונלי) — 960×720.",
  "bundle-2": "תמונת מארז 2 (אופציונלי) — 960×720.",
  "bundle-3": "תמונת מארז 3 (אופציונלי) — 960×720.",
  "hero-video-poster": "תמונת poster לסרטון לפני נגינה — 1600×900 (16:9).",
  "about-portrait": "פורטרט של נבות לדף עלינו — 1200×1600 (3:4). מומלץ בכרם או במשגב עם.",
  "about-misgav-am": "נוף משגב עם / הגליל הצפוני — 1920×1080 (16:9).",
  "about-vineyard": "צילום של הכרם או שדה הגפנים — 1920×1080 (16:9).",
  "about-history": "תמונה היסטורית או משפחתית מההתחלות בכרם — 1200×800 (3:2).",
  "site-logo": "לוגו האתר — PNG/SVG עם רקע שקוף, גובה 80–160 פיקסלים. מוצג בכותרת ובפוטר.",
};

export interface SiteWineAttributeDef {
  id: string;
  label: string;
  /** Screen-reader description announced for the attribute's icon. */
  altText: string;
  /** flag — binary (shown or not); value — parameter whose value is typed
   *  per product (e.g. "אחוז אלכוהול" → "14.5%"). Older rows omit it. */
  kind?: "flag" | "value";
}
export interface SiteWineAttributes {
  items: SiteWineAttributeDef[];
}

export interface SiteContentMap {
  hero: SiteHero;
  video: SiteVideo;
  story: SiteStory;
  featured: SiteFeatured;
  bundles: SiteBundles;
  testimonials: SiteTestimonials;
  features: SiteFeatures;
  about: SiteAbout;
  image: SiteImageBanner;
  privacy: SiteLegalPage;
  accessibility: SiteLegalPage;
  terms: SiteLegalPage;
  contact: SiteContact;
  ageGate: SiteAgeGate;
  spacer: SiteSpacer;
  newsletter: SiteNewsletter;
  footerNewsletter: SiteNewsletter;
  headerConfig: SiteHeaderConfig;
  footerConfig: SiteFooterConfig;
  wineAttributes: SiteWineAttributes;
}

/**
 * Image map keyed by slot. Known legacy slots (SiteImageSlot) are always
 * present (with `null` when no image uploaded); per-instance slots
 * (`<type>-<instanceId>`) appear only when actually uploaded — the wider
 * `Record<string, ...>` accommodates them.
 */
export type SiteImagesMap = Record<string, string | null>;

export interface SiteContentResponse extends SiteContentMap {
  images: SiteImagesMap;
  footerNewsletter: SiteNewsletter;
  headerConfig: SiteHeaderConfig;
  footerConfig: SiteFooterConfig;
}

export interface AdminSiteContentResponse {
  content: {
    [K in SiteContentKey]: { value: SiteContentMap[K]; updatedAt: string | null };
  };
  images: SiteImagesMap;
}

/* ─── New page-based CMS model (WordPress-like) ──────────────────────── */

/** Block types that can live inside a page's block list. Excludes the
 *  global keys (`contact`, `ageGate`) which are edited via /admin/site/content. */
export type PageBlockType =
  | "hero"
  | "video"
  | "story"
  | "featured"
  | "bundles"
  | "testimonials"
  | "features"
  | "about"
  | "image"
  | "spacer"
  | "newsletter"
  | "privacy"
  | "accessibility"
  | "terms";

/** Page slug. Core pages (home/about/privacy/terms/accessibility/tours) are
 *  always present; the admin can create additional ones with any slug
 *  matching `/^[a-z][a-z0-9-]{1,40}$/`. */
export type PageSlug = string;

/** Single instance of a block on a page. `value` is the per-type CMS shape. */
export type BlockInstance = {
  [K in PageBlockType]: {
    id: string;
    type: K;
    visible: boolean;
    value: SiteContentMap[K];
  };
}[PageBlockType];

/* ─── Tours ──────────────────────────────────────────────────────────── */

export interface PublicTour {
  id: number;
  slug: string;
  nameHe: string;
  descriptionHe: string | null;
  durationMinutes: number;
  pricePerPersonAgorot: number;
  maxParticipants: number;
  minParticipants: number;
  imageUrl: string | null;
}

export interface PublicTourSlot {
  id: number;
  startsAt: string;
  available: number;
  capacity: number;
}

export interface AdminTour {
  id: number;
  slug: string;
  nameHe: string;
  descriptionHe: string | null;
  durationMinutes: number;
  pricePerPersonAgorot: number;
  maxParticipants: number;
  minParticipants: number;
  imageUrl: string | null;
  isPublished: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface TourSlot {
  id: number;
  tourId: number;
  startsAt: string;
  capacity: number;
  bookedCount: number;
  isCancelled: boolean;
  notes: string | null;
  createdAt: string;
}

export interface AdminTourBooking {
  id: number;
  publicId: string;
  tourId: number;
  tourName: string | null;
  slotId: number;
  startsAt: string | null;
  customerName: string;
  email: string;
  phone: string;
  participantCount: number;
  totalAgorot: number;
  discountAgorot: number;
  couponCodeSnapshot: string | null;
  notes: string | null;
  status: "pending" | "confirmed" | "cancelled";
  createdAt: string;
}

/** Theme persisted on the server (matches server/site-cms/theme.ts).
 *  `overrides` keys are any of the 18 palette tokens; `headingFont`/`bodyFont`
 *  are optional per-family picks (from FONT_FAMILIES) that override the pair. */
export interface SiteThemeResponse {
  paletteId: string;
  fontPairId: string;
  headingFont?: string;
  bodyFont?: string;
  buttonShape: "pill" | "rounded" | "square";
  buttonFill: "solid" | "outline" | "ghost";
  overrides: Partial<Record<string, string>>;
}
